// libs
import * as React from 'react'

// interfaces / constants
import { CLASS_PREFIX } from 'src/constants/'
import { DETAIL_IMAGE_HEIGHT, DETAIL_IMAGE_WIDTH } from 'src/constants/image'
import { FRONTEND_POST_COMMENTS_ANCHOR } from 'src/constants/urls'

// classes
import { MODE_TYPE_EDIT } from 'src/components/media_input/media_input'
import Comment from 'src/components/post/shared/comments/comment'
import CommentInput from 'src/components/post/shared/comments/comment_input'
import DeletedComment from 'src/components/post/shared/comments/deleted_comment'
import { showSnackBar } from 'src/components/snack_bar/snack_bar'

// helpers
import { CombinedGraphQLErrors } from '@apollo/client/errors'
import {
  CommentFragmentFragment,
  ReactionName,
  useAuthLogin,
  useCreateComment,
  useDeleteComment,
  useUpdateComment,
  useUpdateReaction
} from '@lopo-tech/lopo-api'
import { TextInputHandle } from 'src/components/text_input/text_input'
import { textResources } from 'src/lang/de'
import { useCreateAssetFlow } from 'src/utils/assets/createAsset'
import { useSelectCurrentProfile } from '../../../../reducers/user/user_hooks'
import './comments.scss'

const labels = textResources.comments
const cls = CLASS_PREFIX + 'comments'

type Props = {
  id: string;
  canComment: boolean;
  isCommentable: boolean;
  comments: CommentFragmentFragment[];
};

export function Comments ({ comments, canComment, isCommentable, id }: Props) {
  const [editing, setEditing] = React.useState<CommentFragmentFragment | null>(null)

  const { isAuthenticated } = useAuthLogin()
  const currentProfile = useSelectCurrentProfile()

  const shapeSponsorshipLevel =
    currentProfile?.__typename !== 'IntermediateProfile' &&
    currentProfile?.__typename !== 'PrivateProfile'
      ? currentProfile?.shapeSponsorshipLevel
      : null

  const [updateReaction] = useUpdateReaction()
  const [createComment] = useCreateComment()
  const [updateComment] = useUpdateComment()
  const [deleteComment] = useDeleteComment()
  const createAssetFlow = useCreateAssetFlow()

  const commentInputRef = React.useRef<TextInputHandle | null>(null)
  const commentSectionRef = React.useRef<HTMLDivElement | null>(null)
  const scrollDownIntervalRef = React.useRef<number | null>(null)

  // scroll to comments if hash is present — useLayoutEffect as requested
  React.useLayoutEffect(() => {
    if (window.location.hash === FRONTEND_POST_COMMENTS_ANCHOR) {
      scrollToComments()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const updateCommentInputRef = React.useCallback(
    (ref: TextInputHandle | null) => {
      commentInputRef.current = ref
    },
    []
  )

  // focusCommentInput removed — not used currently
  const setCommentSectionRef = React.useCallback(
    (el: HTMLDivElement | null) => {
      // The ref is on the comment headline in order for scrollIntoView to scroll
      // until a position where he will see both the title and the first comments
      commentSectionRef.current = el
    },
    []
  )

  const scrollToComments = React.useCallback(() => {
    let maxAttempts = 2
    if (scrollDownIntervalRef.current) {
      clearInterval(scrollDownIntervalRef.current)
    }
    scrollDownIntervalRef.current = window.setInterval(() => {
      const el = commentSectionRef.current
      if (!el) {
        maxAttempts = 0
      } else if (el.getBoundingClientRect().top === 0 || maxAttempts === 0) {
        if (scrollDownIntervalRef.current) {
          clearInterval(scrollDownIntervalRef.current)
          scrollDownIntervalRef.current = null
        }
      } else {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' })
      }
      maxAttempts--
    }, 1000)
  }, [])

  const handleCancelEditing = React.useCallback(() => {
    // TODO: Add tracking again
    // const { tracker, trackingObj } = tracking
    // tracker(trackingObj.ACTIONS.UPDATE, trackingObj.LABELS.CANCEL)
    setEditing(null)
  }, [])

  const handleDeleteComment = React.useCallback(
    async (commentId: string) => {
      try {
        await deleteComment({
          variables: {
            input: { id: commentId }
          }
        })
      } catch (errors) {
        if (errors && CombinedGraphQLErrors.is(errors)) {
          showSnackBar(errors.message)
        }
      }
    },
    [id, deleteComment]
  )

  const handleEditComment = React.useCallback(
    (comment: CommentFragmentFragment) => {
      setEditing(comment)
    },
    []
  )

  const handleLikeClick = React.useCallback(
    async (commentId: string) => {
      try {
        await updateReaction({
          variables: {
            input: {
              reactableId: commentId,
              reactionName: ReactionName.OldHeart
            }
          }
        })
      } catch (errors) {
        if (errors && CombinedGraphQLErrors.is(errors)) {
          showSnackBar(errors.message)
        }
      }
    },
    [id, updateReaction]
  )

  const submitCommentLocal = React.useCallback(
    async (message: string, images?: string[], oglinkId?: string) => {
      try {
        const assetIds = await createAssetFlow(images)
        await createComment({
          variables: {
            input: {
              message,
              commentableId: id,
              assetIds,
              oglinkId
            },
            // The sizes the detail query reads `previewUrl` with — without them
            // lopo-api leaves a comment that has an attachment out of the cached
            // list, while still counting it.
            height: DETAIL_IMAGE_HEIGHT,
            width: DETAIL_IMAGE_WIDTH
          }
        })
      } catch (errors) {
        if (errors && CombinedGraphQLErrors.is(errors)) {
          showSnackBar(errors.message)
        }
      }
    },
    [id, createComment]
  )

  const updateCommentLocal = React.useCallback(
    async (
      commentId: string,
      message: string,
      oldImages: string[],
      newImages: string[],
      oglinkId?: string
    ) => {
      if (!editing) {
        return
      }
      setEditing(null)

      const assetIds = [...oldImages, ...(await createAssetFlow(newImages))]
      await updateComment({
        variables: {
          input: {
            id: commentId,
            message,
            assetIds,
            oglinkId
          }
        }
      })
    },
    [editing, id]
  )

  const renderTopElementCommentable = React.useCallback(
    (hasComments: boolean) => <>{!hasComments && labels.noneYet_WEB}</>,
    []
  )

  const renderCommentSubheadline = React.useCallback(
    (hasComments: boolean) =>
      hasComments
        ? (
          <></>
        )
        : (
          <div className={cls + '__top__subheadline'}>
            <span>{labels.nonYetMessage}</span>
          </div>
        ),
    []
  )

  const renderCommentEdit = React.useCallback(
    (editingItem: CommentFragmentFragment) => {
      return (
        <CommentInput
          cancel={handleCancelEditing}
          commentId={editingItem.id}
          disabled={!canComment}
          attachements={editingItem.attachments}
          message={editingItem.message}
          mode={MODE_TYPE_EDIT}
          oglink={editingItem.oglink}
          sponsorLevel={shapeSponsorshipLevel}
          update={updateCommentLocal}
          userImageUrl={currentProfile?.avatar.previewUrl}
        />
      )
    },
    [
      canComment,
      shapeSponsorshipLevel,
      handleCancelEditing,
      updateCommentLocal,
      currentProfile?.avatar.previewUrl
    ]
  )

  const renderCommentCreate = React.useCallback(
    (): JSX.Element | null =>
      canComment
        ? (
          <CommentInput
            disabled={!canComment}
            submit={submitCommentLocal}
            userImageUrl={currentProfile?.avatar.previewUrl}
            update={updateCommentLocal}
            refProp={updateCommentInputRef}
            sponsorLevel={shapeSponsorshipLevel}
          />
        )
        : null,
    [
      canComment,
      currentProfile?.avatar.previewUrl,
      updateCommentLocal,
      shapeSponsorshipLevel
    ]
  )

  const renderComment = React.useCallback(
    (comment: CommentFragmentFragment, index: number) => {
      if (comment.isDeleted) {
        return <DeletedComment key={index} comment={comment} />
      }
      if (editing && editing.id === comment.id) {
        return (
          <div key={index} className={cls + '__edit-desktop'}>
            {renderCommentEdit(editing)}
          </div>
        )
      }
      return (
        <Comment
          deleteComment={handleDeleteComment}
          editComment={handleEditComment}
          key={index}
          likeToggle={handleLikeClick}
          comment={comment}
          isLoggedIn={isAuthenticated}
        />
      )
    },
    [
      editing,
      renderCommentEdit,
      handleDeleteComment,
      handleEditComment,
      handleLikeClick
    ]
  )

  return (
    <div className={cls}>
      <div className={cls + '__top'} ref={setCommentSectionRef}>
        <h2 className={cls + '__top__headline'}>
          {isCommentable
            ? renderTopElementCommentable(!!comments.length)
            : labels.disabled}
        </h2>
        {canComment ? renderCommentSubheadline(!!comments.length) : null}
      </div>
      <div className={cls + '__list'}>{comments.map(renderComment)}</div>
      {editing
        ? (
          <div className={cls + '__edit-mobile'}>
            {renderCommentEdit(editing)}
          </div>
        )
        : (
          renderCommentCreate()
        )}
    </div>
  )
}
